import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { PageLoadingSkeleton } from "../common/LoadingSkeleton";
import { sanitizePath, isSafeInternalPath } from "../../utils/routingUtils";
import { isAllowedRedirectPath } from "../../constants/routing";

/**
 * RequireAuth component - Simple route protection
 * Redirects unauthenticated users to login page
 * 
 * Note: For more advanced features (fallback UI, custom redirects),
 * use AuthGuard component instead.
 */
export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();
  const loc = useLocation();

  // SECURITY: Show loading state while checking authentication
  // Prevents flickering and ensures consistent behavior with AuthGuard
  if (loading) {
    return <PageLoadingSkeleton />;
  }

  if (!currentUser) {
    // SECURITY: Sanitize and validate return URL to prevent open redirect attacks
    const raw = `${loc.pathname}${loc.search || ''}`;
    const candidate = sanitizePath(raw, 500);
    
    // SECURITY: Validate path is safe and in whitelist
    const isSafe = isSafeInternalPath(candidate) && isAllowedRedirectPath(candidate);
    const redirectTo = isSafe ? candidate : '/';
    
    return <Navigate to="/login" state={{ redirectTo }} replace />;
  }
  
  return <>{children}</>;
}



